import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Search, RefreshCw, UserCog, UserMinus } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { aplicacionesAPI, usuariosAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import LogoApp from '../components/LogoApp';
import Paginador from '../components/Paginador';
import { fechaHora, haceCuanto, soloFecha } from '../utils/tiempo';

const TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'usuarios', label: 'Usuarios' },
  { id: 'accesos', label: 'Registros' },
  { id: 'cambios', label: 'Cambios de permisos' },
];
const POR_PAGINA = 12;

const Dato = ({ etiqueta, valor, tono }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
    <p className="text-xs text-gray-500 dark:text-gray-400">{etiqueta}</p>
    <p className={`text-2xl font-bold mt-0.5 ${tono === 'alerta' ? 'text-red-600' : tono === 'aviso' ? 'text-amber-600' : 'text-gray-900 dark:text-white'}`}>{valor}</p>
  </div>
);

const Insignia = ({ children, tono = 'gris' }) => {
  const clases = {
    verde: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    azul: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    rojo: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    ambar: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
    gris: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${clases[tono]}`}>{children}</span>;
};

const Tabla = ({ columnas, children, vacio }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <tr>{columnas.map((c) => <th key={c} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{c}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">{children}</tbody>
    </table>
    {vacio}
  </div>
);

const totalPaginas = (total) => Math.max(1, Math.ceil(total / POR_PAGINA));
const rebanar = (lista, pagina) => {
  const actual = Math.min(pagina, totalPaginas(lista.length));
  return lista.slice((actual - 1) * POR_PAGINA, actual * POR_PAGINA);
};
function PaginadorLista({ total, pagina, onChange, etiqueta }) {
  const actual = Math.min(pagina, totalPaginas(total));
  return (
    <Paginador
      pagina={actual}
      totalPaginas={totalPaginas(total)}
      total={total}
      desde={total ? (actual - 1) * POR_PAGINA + 1 : 0}
      hasta={Math.min(actual * POR_PAGINA, total)}
      onChange={onChange}
      etiqueta={etiqueta}
    />
  );
}

function resumirNavegador(ua) {
  const t = String(ua || '');
  if (!t) return '—';
  if (/^node/i.test(t)) return 'Servidor de la app (node)';
  const candidatos = [['Edge', /Edg\/(\d+)/], ['Opera', /OPR\/(\d+)/], ['Firefox', /Firefox\/(\d+)/], ['Chrome', /Chrome\/(\d+)/], ['Safari', /Version\/(\d+).*Safari/]];
  const hallado = candidatos.map(([n, re]) => [n, t.match(re)]).find(([, m]) => m);
  const so = /Windows/.test(t) ? 'Windows' : /iPhone|iPad/.test(t) ? 'iOS' : /Android/.test(t) ? 'Android' : /Mac OS X/.test(t) ? 'macOS' : /Linux/.test(t) ? 'Linux' : null;
  return [hallado ? `${hallado[0]} ${hallado[1][1]}` : t.slice(0, 40), so].filter(Boolean).join(' · ');
}

const MOTIVOS = {
  SIN_ACCESO_APP: 'Sin acceso a la app',
  CREDENCIALES_INVALIDAS: 'Clave incorrecta o cuenta inexistente',
  CUENTA_DESVINCULADA: 'Cuenta desvinculada',
  CUENTA_SOLO_SSO: 'La cuenta usa Microsoft, no clave',
  CUENTA_SOLO_LOCAL: 'La cuenta usa clave, no Microsoft',
  DOMINIO_NO_PERMITIDO: 'Correo de un dominio no permitido',
  CUENTA_NO_ENCONTRADA_O_INACTIVA: 'Cuenta inexistente o desactivada',
};
const motivoTexto = (m) => MOTIVOS[m] || m || 'Rechazado';

const FILTROS = [
  { id: 'todos', label: 'Todas' },
  { id: 'inactivas', label: 'Cuentas desactivadas' },
  { id: 'sin-actividad', label: 'Sin actividad (30 d)' },
  { id: 'mfa', label: 'Verificación pendiente' },
];
const cumpleFiltro = (u, f) => {
  if (f === 'inactivas') return !u.cuentaActiva;
  if (f === 'sin-actividad') return u.cuentaActiva && u.sinActividad30d === true;
  if (f === 'mfa') return u.cuentaActiva && (u.metodo === 'local' || u.metodo === 'ambos') && !u.mfaActivo;
  return true;
};
const contarFiltro = (lista, f) => lista.filter((u) => cumpleFiltro(u, f)).length;

function GraficoDias({ datos, nombres = ['Exitosos', 'Fallidos'] }) {
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={datos} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis dataKey="dia" tickFormatter={(v) => `${v.slice(8, 10)}/${v.slice(5, 7)}`} tick={{ fontSize: 11 }} interval={4} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip labelFormatter={(v) => soloFecha(v)} />
          <Bar dataKey="ok" name={nombres[0]} stackId="a" fill="#2563eb" radius={[2, 2, 0, 0]} />
          <Bar dataKey="err" name={nombres[1]} stackId="a" fill="#f59e0b" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function AplicacionDetalle() {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'resumen';

  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const filtro = FILTROS.some((f) => f.id === searchParams.get('filtro')) ? searchParams.get('filtro') : 'todos';
  const [paginaUsuarios, setPaginaUsuarios] = useState(1);
  const [paginaEventos, setPaginaEventos] = useState(1);
  const [paginaAcciones, setPaginaAcciones] = useState(1);
  const [vistaRegistro, setVistaRegistro] = useState('ingresos');
  const [soloCambios, setSoloCambios] = useState(false);
  const [filtroIngreso, setFiltroIngreso] = useState('todos');
  const [busquedaRegistro, setBusquedaRegistro] = useState('');
  const [paginaCambios, setPaginaCambios] = useState(1);
  const [quitar, setQuitar] = useState(null);
  const [quitando, setQuitando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await aplicacionesAPI.getDetalle(codigo);
      setD(data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo cargar la aplicación');
    } finally {
      setCargando(false);
    }
  }, [codigo]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarTab = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const irAUsuarios = (f) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'usuarios');
    if (f && f !== 'todos') next.set('filtro', f); else next.delete('filtro');
    setPaginaUsuarios(1);
    setSearchParams(next, { replace: true });
  };

  const usuarios = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (d?.usuarios || []).filter((u) => cumpleFiltro(u, filtro) && (!q || `${u.nombre} ${u.email} ${u.perfil || ''}`.toLowerCase().includes(q)));
  }, [d, busqueda, filtro]);

  // Cuentas que conviene revisar, con nombre y apellido (no solo el número)
  const revisar = useMemo(() => {
    const todos = d?.usuarios || [];
    return [
      { id: 'inactivas', filtro: 'inactivas', tono: 'rojo', titulo: 'Cuentas desactivadas que aún tienen acceso', lista: todos.filter((u) => cumpleFiltro(u, 'inactivas')) },
      { id: 'sin-actividad', filtro: 'sin-actividad', tono: 'ambar', titulo: 'Sin actividad en 30 días', lista: todos.filter((u) => cumpleFiltro(u, 'sin-actividad')) },
      { id: 'mfa', filtro: 'mfa', tono: 'ambar', titulo: 'Verificación en dos pasos pendiente', lista: todos.filter((u) => cumpleFiltro(u, 'mfa')) },
    ].filter((g) => g.lista.length > 0);
  }, [d]);

  const confirmarQuitar = async () => {
    setQuitando(true);
    try {
      for (const id of quitar.permisoIds) await usuariosAPI.revocarPermiso(id);
      showToast('Listo', `Se quitó el acceso de ${quitar.nombre}`, 'success');
      setQuitar(null);
      await cargar();
    } catch (e) {
      showToast('Error', e.response?.data?.message || 'No se pudo quitar el acceso', 'error');
    } finally {
      setQuitando(false);
    }
  };

  if (cargando && !d) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" /></div>;
  }
  if (error && !d) {
    return (
      <div className="space-y-4">
        <button type="button" onClick={() => navigate('/aplicaciones')} className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:underline"><ArrowLeft className="w-4 h-4" />Volver a Aplicaciones</button>
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
      </div>
    );
  }

  const { app, resumen, roles, serie, eventos, cambios, trafico } = d;

  return (
    <div className="space-y-6 pb-8">
      <button type="button" onClick={() => navigate('/aplicaciones')} className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:underline">
        <ArrowLeft className="w-4 h-4" />Aplicaciones
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <LogoApp codigo={app.codigo} activa={app.activa} tam="w-16 h-16" />
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{app.nombre}</h1>
              <Insignia tono={app.activa ? 'verde' : 'gris'}>{app.activa ? 'Activa' : 'Inactiva'}</Insignia>
            </div>
            <p className="text-gray-600 dark:text-gray-400">{app.descripcion || 'Aplicación integrada del ecosistema'}</p>
            {app.url && (
              <a href={app.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-1 text-xs font-mono text-blue-600 dark:text-blue-400 hover:underline break-all">
                {app.url}<ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            )}
          </div>
        </div>
        <button type="button" onClick={cargar} disabled={cargando} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${cargando ? 'animate-spin' : ''}`} />Actualizar
        </button>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Secciones">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => cambiarTab(t.id)}
              className={`py-2.5 px-1 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
            >
              {t.label}
              {t.id === 'usuarios' && <span className="ml-1.5 text-xs text-gray-400">({d.usuarios.length})</span>}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'resumen' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
            <Dato etiqueta="Personas con acceso" valor={resumen.usuariosConAcceso} />
            <Dato etiqueta="Acceso total" valor={resumen.accesoTotal} />
            <Dato etiqueta="Por empresa/cliente" valor={resumen.accesoGranular} />
            <Dato etiqueta="Accesos nuevos (30 d)" valor={resumen.nuevos30d} />
            <Dato etiqueta="Ingresos hoy" valor={resumen.accesosHoy} />
            <Dato etiqueta="Ingresos 7 días" valor={resumen.accesos7d} />
            <Dato etiqueta="Ingresos 30 días" valor={resumen.accesos30d} />
            <Dato etiqueta="Fallidos 30 días" valor={resumen.fallidos30d} tono={resumen.fallidos30d > 0 ? 'aviso' : undefined} />
            {resumen.sinActividad30d != null && <Dato etiqueta="Sin actividad (30 d)" valor={resumen.sinActividad30d} tono={resumen.sinActividad30d > 0 ? 'aviso' : undefined} />}
            <Dato etiqueta="Verificación pendiente" valor={resumen.mfaPendientes} tono={resumen.mfaPendientes > 0 ? 'aviso' : undefined} />
            <Dato etiqueta="Cuentas inactivas con acceso" valor={resumen.cuentasInactivasConAcceso} tono={resumen.cuentasInactivasConAcceso > 0 ? 'alerta' : undefined} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-stretch">
            <section className="xl:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Ingresos por día (30 días)</h2>
                <p className="text-xs text-gray-400">Último ingreso: {resumen.ultimoAcceso ? `${haceCuanto(resumen.ultimoAcceso)} (${fechaHora(resumen.ultimoAcceso)})` : 'sin registros'}</p>
              </div>
              <GraficoDias datos={serie} />
              {resumen.accesos30d === 0 && trafico && <p className="mt-2 text-xs text-gray-500">Esta app no registra ingresos por el login central; su actividad se mide con el tráfico de la API (abajo).</p>}
            </section>

            <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-5">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Roles y perfiles</h2>
                <div className="flex flex-wrap gap-1.5">
                  {roles.map((r) => <Insignia key={r.rol} tono="azul">{r.nombre}</Insignia>)}
                  {Object.entries(resumen.perfiles || {}).map(([p, n]) => <Insignia key={p}>{p}: {n}</Insignia>)}
                </div>
                {roles.length === 0 && <p className="text-xs text-gray-500 mt-2">Esta app no define roles propios en la identidad central.</p>}
              </div>

              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Para revisar</h2>
                {revisar.length === 0 ? (
                  <p className="text-sm text-green-700 dark:text-green-300">Sin alertas.</p>
                ) : (
                  <div className="space-y-4">
                    {revisar.map((g) => (
                      <div key={g.id}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <p className={`text-xs font-semibold ${g.tono === 'rojo' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>{g.titulo} ({g.lista.length})</p>
                          {g.lista.length > 5 && <button type="button" onClick={() => irAUsuarios(g.filtro)} className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline">Ver todos</button>}
                        </div>
                        <ul className="space-y-1.5">
                          {g.lista.slice(0, 5).map((u) => (
                            <li key={u.usuarioId} className="flex items-center justify-between gap-2 text-sm">
                              <span className="min-w-0">
                                <span className="block text-gray-900 dark:text-white truncate">{u.nombre}</span>
                                <span className="block text-[11px] text-gray-500 truncate">{u.email}{g.id === 'sin-actividad' && u.ultimaActividad ? ` · última ${haceCuanto(u.ultimaActividad)}` : ''}</span>
                              </span>
                              <button type="button" onClick={() => setQuitar(u)} className="shrink-0 px-2 py-1 text-[11px] font-semibold rounded-md bg-red-50 hover:bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400">Quitar</button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>

          {d.matriz && d.matriz.roles.length > 0 && (
            <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Perfiles y capacidades</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">{d.porEmpresa ? 'Cada persona tiene un perfil por empresa; esto es lo que permite cada uno.' : 'Lo que permite cada perfil.'}</p>
              </div>
              <Tabla columnas={['Capacidad', ...d.matriz.roles.map((r) => r.nombre)]}>
                {d.matriz.permisos.map((pe) => (
                  <tr key={pe.permiso} className="text-gray-700 dark:text-gray-300">
                    <td className="px-4 py-2">{pe.descripcion}</td>
                    {d.matriz.roles.map((r) => <td key={r.rol} className="px-4 py-2">{r.permisos.includes(pe.permiso) ? <span className="text-green-600 font-bold">✓</span> : <span className="text-gray-300">—</span>}</td>)}
                  </tr>
                ))}
              </Tabla>
            </section>
          )}

          {trafico && (
            <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Tráfico de la API (30 días)</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Solicitudes que esta app hizo a la API central. Sirve para ver cuánto se usa y si falla.</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Dato etiqueta="Solicitudes" valor={trafico.solicitudes30d.toLocaleString('es-CL')} />
                <Dato etiqueta="Con error" valor={`${trafico.errores30d.toLocaleString('es-CL')} (${trafico.solicitudes30d ? ((trafico.errores30d / trafico.solicitudes30d) * 100).toFixed(1) : 0}%)`} tono={trafico.errores30d > 0 ? 'aviso' : undefined} />
                <Dato etiqueta="Tiempo promedio" valor={`${trafico.promedioMs} ms`} tono={trafico.promedioMs > 1000 ? 'aviso' : undefined} />
                <Dato etiqueta="Personas activas" valor={trafico.personasActivas} />
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
                <GraficoDias datos={trafico.porDia} nombres={['Correctas', 'Con error']} />
                <Tabla columnas={['Ruta más usada', 'Solicitudes', 'Errores', 'Promedio']}>
                  {trafico.topRutas.map((r) => (
                    <tr key={r.ruta} className="text-gray-700 dark:text-gray-300">
                      <td className="px-4 py-2 font-mono text-xs break-all">{r.ruta}</td>
                      <td className="px-4 py-2 tabular-nums">{r.solicitudes.toLocaleString('es-CL')}</td>
                      <td className={`px-4 py-2 tabular-nums ${r.errores > 0 ? 'text-amber-600' : ''}`}>{r.errores}</td>
                      <td className="px-4 py-2 tabular-nums whitespace-nowrap">{r.promedioMs} ms</td>
                    </tr>
                  ))}
                </Tabla>
              </div>
            </section>
          )}
        </div>
      )}

      {tab === 'usuarios' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPaginaUsuarios(1); }} placeholder="Buscar por nombre, correo o perfil…" className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {FILTROS.filter((f) => f.id === 'todos' || (f.id === 'sin-actividad' ? resumen.sinActividad30d != null : true)).map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => irAUsuarios(f.id)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${filtro === f.id ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  {f.label}{f.id !== 'todos' && ` (${contarFiltro(d.usuarios, f.id)})`}
                </button>
              ))}
            </div>
          </div>
          <Tabla
            columnas={['Persona', 'Perfil', 'Alcance', 'Cuenta', 'Verificación', ...(d.actividadDisponible ? ['Última actividad', 'Actividad 30 d'] : []), ...(d.usuarios.some((x) => x.ingresos30d != null) ? ['Ingresos 30 d'] : []), 'Acceso desde', '']}
            vacio={usuarios.length === 0 ? <p className="p-6 text-center text-sm text-gray-500">No hay personas que coincidan.</p> : null}
          >
            {rebanar(usuarios, paginaUsuarios).map((u) => (
              <tr key={u.usuarioId} className="text-gray-700 dark:text-gray-300">
                <td className="px-4 py-3"><p className="font-medium text-gray-900 dark:text-white">{u.nombre}</p><p className="text-xs text-gray-500">{u.email}</p></td>
                <td className="px-4 py-3 capitalize">{u.perfil || '—'}</td>
                <td className="px-4 py-3">{u.accesoTotal ? <Insignia tono="verde">Total</Insignia> : d.porEmpresa ? <div className="flex flex-wrap gap-1">{u.alcance.map((a) => <Insignia key={a.permisoId} tono="azul">{a.empresa} · {a.perfil || 'sin perfil'}</Insignia>)}</div> : <Insignia tono="azul">{u.combinaciones} empresa/cliente</Insignia>}</td>
                <td className="px-4 py-3">{u.cuentaActiva ? <Insignia tono="verde">Activa</Insignia> : <Insignia tono="rojo">Desactivada</Insignia>}</td>
                <td className="px-4 py-3">{u.metodo === 'local' || u.metodo === 'ambos' ? (u.mfaActivo ? <Insignia tono="verde">Autenticador</Insignia> : <Insignia tono="ambar">Pendiente</Insignia>) : <span className="text-xs text-gray-400">Microsoft</span>}</td>
                {d.actividadDisponible && (
                  <>
                    <td className="px-4 py-3 whitespace-nowrap">{u.ultimaActividad ? <><p>{haceCuanto(u.ultimaActividad)}</p><p className="text-xs text-gray-400">{soloFecha(u.ultimaActividad)}</p></> : <span className="text-xs text-amber-600">Sin registro</span>}</td>
                    <td className="px-4 py-3 tabular-nums">{u.actividad30d}{u.errores30d > 0 && <span className="ml-1 text-xs text-amber-600">({u.errores30d} con error)</span>}</td>
                  </>
                )}
                {d.usuarios.some((x) => x.ingresos30d != null) && <td className="px-4 py-3 tabular-nums">{u.ingresos30d ?? 0}</td>}
                <td className="px-4 py-3 whitespace-nowrap"><p>{soloFecha(u.otorgadoEl)}</p>{u.otorgadoPor && <p className="text-xs text-gray-400">por {u.otorgadoPor}</p>}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {u.nominaId && (
                      <button type="button" onClick={() => navigate(`/usuarios/${u.nominaId}?tab=aplicaciones`)} title="Ver permisos del colaborador" className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200">
                        <UserCog className="w-3.5 h-3.5" />Permisos
                      </button>
                    )}
                    <button type="button" onClick={() => setQuitar(u)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-red-50 hover:bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400">
                      <UserMinus className="w-3.5 h-3.5" />Quitar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </Tabla>
          <PaginadorLista total={usuarios.length} pagina={paginaUsuarios} onChange={setPaginaUsuarios} etiqueta="personas" />
          {d.actividadDisponible && <p className="text-xs text-gray-500 dark:text-gray-400">{d.fuenteIngresos === 'login' ? 'La actividad son los inicios de sesión en HelpDesk.' : 'La actividad son las solicitudes de la persona a la API central desde esta app (últimos 90 días para la fecha, 30 para el conteo).'}</p>}
        </div>
      )}

      {tab === 'accesos' && (() => {
        const conPersona = d.fuenteIngresos !== 'sso';
        const q = busquedaRegistro.trim().toLowerCase();
        const coincide = (txt) => !q || String(txt || '').toLowerCase().includes(q);

        const ingresos = eventos.filter((e) => (filtroIngreso === 'todos' || (filtroIngreso === 'ok' ? e.exito : !e.exito)) && coincide(`${e.persona} ${e.email} ${e.ip}`));
        const rechazados = eventos.filter((e) => !e.exito);
        const personas = new Set(eventos.filter((e) => e.email).map((e) => e.email.toLowerCase())).size;
        const cuentasFallidas = [...new Set(rechazados.map((e) => (e.email || '').toLowerCase()).filter(Boolean))];

        const base = d.acciones ? (soloCambios ? d.acciones.cambios : d.acciones.todas) : [];
        const lista = base.filter((a) => coincide(`${a.persona} ${a.email} ${a.accion} ${a.detalle}`));
        const hayAcciones = (d.acciones?.todas?.length || 0) > 0;

        return (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
                {[['ingresos', `Ingresos (${eventos.length})`], ['acciones', `Acciones (${d.acciones?.todas?.length || 0})`]].map(([id, texto]) => (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={vistaRegistro === id}
                    onClick={() => setVistaRegistro(id)}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${vistaRegistro === id ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                  >
                    {texto}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={busquedaRegistro}
                  onChange={(e) => { setBusquedaRegistro(e.target.value); setPaginaEventos(1); setPaginaAcciones(1); }}
                  placeholder="Buscar por persona, correo, IP o acción…"
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {vistaRegistro === 'ingresos' && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Dato etiqueta="Ingresos (30 d)" valor={eventos.length - rechazados.length} />
                  <Dato etiqueta="Intentos rechazados" valor={rechazados.length} tono={rechazados.length > 0 ? 'aviso' : undefined} />
                  <Dato etiqueta="Cuentas con intentos fallidos" valor={cuentasFallidas.length} tono={cuentasFallidas.length > 0 ? 'aviso' : undefined} />
                  <Dato etiqueta="Personas distintas" valor={conPersona ? personas : '—'} />
                </div>

                {d.fuenteIngresos === 'sso' && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
                    Estos ingresos son anónimos: api_vicsa todavía no registra quién entró. Cuando se despliegue su registro de accesos, aquí aparecerán la persona, la IP y el navegador reales y los intentos rechazados con la cuenta usada.
                  </p>
                )}
                {d.fuenteIngresos === 'registro' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Ingresos e intentos rechazados de los últimos 30 días, con la cuenta usada, desde dónde y por qué vía.</p>
                )}
                {d.fuenteIngresos === 'login' && <p className="text-xs text-gray-500 dark:text-gray-400">Inicios de sesión en HelpDesk de los últimos 30 días, con la persona que ingresó.</p>}

                <div className="flex flex-wrap gap-1.5">
                  {[['todos', 'Todos'], ['ok', 'Exitosos'], ['fallo', 'Rechazados']].map(([id, texto]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => { setFiltroIngreso(id); setPaginaEventos(1); }}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${filtroIngreso === id ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                    >
                      {texto}
                    </button>
                  ))}
                </div>

                <Tabla
                  columnas={['Fecha y hora', 'Cuenta', 'Resultado', ...(d.fuenteIngresos === 'registro' ? ['Vía'] : []), 'IP', 'Navegador']}
                  vacio={ingresos.length === 0 ? <p className="p-6 text-center text-sm text-gray-500">Sin ingresos con ese filtro en los últimos 30 días.{trafico ? ' Mira su actividad en «Resumen» y «Usuarios».' : ''}</p> : null}
                >
                  {rebanar(ingresos, paginaEventos).map((e, i) => (
                    <tr key={`${e.fecha}-${i}`} className={`text-gray-700 dark:text-gray-300 ${e.exito ? '' : 'bg-red-50/40 dark:bg-red-950/10'}`}>
                      <td className="px-4 py-3 whitespace-nowrap">{fechaHora(e.fecha)}</td>
                      <td className="px-4 py-3">
                        {e.persona || e.email ? (
                          <>
                            <p className="font-medium text-gray-900 dark:text-white">{e.persona || e.email}</p>
                            {e.persona && <p className="text-xs text-gray-500">{e.email}</p>}
                            {!e.persona && !e.exito && <p className="text-[11px] text-gray-400">cuenta no registrada</p>}
                          </>
                        ) : <span className="text-gray-400">{conPersona ? '—' : 'No registrada'}</span>}
                      </td>
                      <td className="px-4 py-3">
                        {e.exito ? <Insignia tono="verde">Exitoso</Insignia> : <Insignia tono="rojo">Rechazado</Insignia>}
                        {!e.exito && <p className="text-xs text-gray-500 mt-0.5 max-w-xs">{motivoTexto(e.motivo || e.error)}</p>}
                      </td>
                      {d.fuenteIngresos === 'registro' && <td className="px-4 py-3 text-xs">{e.via}</td>}
                      <td className="px-4 py-3 font-mono text-xs">{e.ip || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate" title={e.navegador}>{resumirNavegador(e.navegador)}</td>
                    </tr>
                  ))}
                </Tabla>
                <PaginadorLista total={ingresos.length} pagina={paginaEventos} onChange={setPaginaEventos} etiqueta="ingresos" />
              </>
            )}

            {vistaRegistro === 'acciones' && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {codigo === 'helpdesk'
                      ? 'Lo que hicieron las personas en HelpDesk en los últimos 30 días (altas, cambios, firmas…).'
                      : 'Lo que hicieron las personas en la app (últimos 30 días): las acciones que la app informa y las solicitudes que hace a la API central.'}
                  </p>
                  {codigo !== 'helpdesk' && (
                    <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={soloCambios} onChange={(e) => { setSoloCambios(e.target.checked); setPaginaAcciones(1); }} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      Solo las que modifican datos
                    </label>
                  )}
                </div>

                {!hayAcciones && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
                    Esta app todavía no informa sus acciones. Para verlas aquí, su servidor debe enviarlas a api_vicsa:{' '}
                    <code className="font-mono">POST /api/apps/{codigo}/acciones</code> con <code className="font-mono">{'{ email, accion, detalle }'}</code> (API Key con el permiso <code className="font-mono">apps:registrar</code>).
                  </p>
                )}

                <Tabla
                  columnas={['Fecha y hora', 'Persona', 'Acción', ...(codigo !== 'helpdesk' ? ['Resultado'] : []), 'IP', 'Navegador']}
                  vacio={lista.length === 0 ? <p className="p-6 text-center text-sm text-gray-500">Sin acciones con ese filtro.</p> : null}
                >
                  {rebanar(lista, paginaAcciones).map((a, i) => (
                    <tr key={`${a.fecha}-${i}`} className="text-gray-700 dark:text-gray-300">
                      <td className="px-4 py-3 whitespace-nowrap">{fechaHora(a.fecha)}</td>
                      <td className="px-4 py-3">{a.persona || a.email ? <><p className="font-medium text-gray-900 dark:text-white">{a.persona || a.email}</p>{a.persona && <p className="text-xs text-gray-500">{a.email}</p>}</> : <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-3">
                        <p className="font-mono text-xs break-all">{a.accion}</p>
                        {a.detalle && <p className="text-xs text-gray-500 mt-0.5">{a.detalle}</p>}
                        {a.origen === 'app' && <span className="text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400">informada por la app</span>}
                      </td>
                      {codigo !== 'helpdesk' && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          {a.estado != null ? (a.estado >= 200 && a.estado < 400 ? <Insignia tono="verde">{a.estado}</Insignia> : <Insignia tono="rojo">{a.estado}</Insignia>)
                            : a.exito === true ? <Insignia tono="verde">Correcta</Insignia> : a.exito === false ? <Insignia tono="rojo">Falló</Insignia> : <span className="text-gray-400">—</span>}
                          {a.duracionMs != null && <span className="ml-2 text-xs text-gray-400 tabular-nums">{a.duracionMs} ms</span>}
                        </td>
                      )}
                      <td className="px-4 py-3 font-mono text-xs">{a.ip || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate" title={a.navegador}>{resumirNavegador(a.navegador)}</td>
                    </tr>
                  ))}
                </Tabla>
                <PaginadorLista total={lista.length} pagina={paginaAcciones} onChange={setPaginaAcciones} etiqueta="acciones" />
              </>
            )}
          </div>
        );
      })()}

      {tab === 'cambios' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Accesos otorgados y revocados a esta app, del más reciente al más antiguo.</p>
          <Tabla columnas={['Fecha', 'Cambio', 'Persona', 'Perfil', 'Alcance', 'Hecho por']} vacio={cambios.length === 0 ? <p className="p-6 text-center text-sm text-gray-500">Sin cambios registrados.</p> : null}>
            {rebanar(cambios, paginaCambios).map((c, i) => (
              <tr key={`${c.fecha}-${c.email}-${i}`} className="text-gray-700 dark:text-gray-300">
                <td className="px-4 py-3 whitespace-nowrap">{fechaHora(c.fecha)}</td>
                <td className="px-4 py-3">{c.tipo === 'otorgado' ? <Insignia tono="verde">Acceso otorgado</Insignia> : <Insignia tono="rojo">Acceso revocado</Insignia>}</td>
                <td className="px-4 py-3"><p className="font-medium text-gray-900 dark:text-white">{c.nombre}</p><p className="text-xs text-gray-500">{c.email}</p></td>
                <td className="px-4 py-3 capitalize">{c.perfil || '—'}</td>
                <td className="px-4 py-3">{c.alcance}</td>
                <td className="px-4 py-3">{c.por || <span className="text-gray-400">—</span>}</td>
              </tr>
            ))}
          </Tabla>
          <PaginadorLista total={cambios.length} pagina={paginaCambios} onChange={setPaginaCambios} etiqueta="cambios" />
        </div>
      )}

      {quitar && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-[9000] bg-black/50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !quitando) setQuitar(null); }}>
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Quitar acceso</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {quitar.nombre} ya no podrá ingresar a <b>{app.nombre}</b>. Puedes volver a darle acceso desde sus permisos.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" disabled={quitando} onClick={() => setQuitar(null)} className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600">Cancelar</button>
              <button type="button" disabled={quitando} onClick={confirmarQuitar} className="px-4 py-2 text-sm font-bold rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">{quitando ? 'Quitando…' : 'Quitar acceso'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
